import LayoutContainer from 'components/Common/LayoutContainer'
import Heading from 'LuxKit/Typography/Heading'
import Group from 'components/utils/Group'
import CarHireBundledUpcomingOrderCarousel from './CarHireBundledUpcomingOrderCarousel'
import useOrderItemsEligibleForCarHireBundle from 'hooks/CarHire/useOrderItemsEligibleForCarHireBundle'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import { useCallback, useMemo } from 'react'
import TextButton from 'LuxKit/Button/TextButton'
import { rem } from 'polished'
import styled from 'styled-components'
import { pushWithRegion } from 'actions/NavigationActions'
import { useAppDispatch } from 'hooks/reduxHooks'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { getLoyaltyBonusPointsCampaignTypes } from 'loyalty/lib/determineLoyaltyBonusPointsCampaignType'
import { LOYALTY_BONUS_POINTS_CAMPAIGN_LABEL } from 'loyalty/constants/loyaltyBonusPoints'
import { useIntl } from 'react-intl'
import { formatPlacePhrase } from '@luxuryescapes/lib-location-grammar'

interface Props {
  order: App.Order
  variant?: 'card' | 'full-width'
}

const MainContainer = styled(Group)`
  display: grid;
  grid-template-columns: 1fr auto;
`

const ViewAllButtonWrapper = styled(Group)`
  align-self: end;
  padding: ${rem(10)} ${rem(12)};

  ${mediaQueryUp.tablet} {
    padding: 0;
  }
`

function CarHireBundledUpcomingOrderCondensed(props: Props) {
  const { order, variant } = props
  const intl = useIntl()

  const { place, fetching, searchLink, benefits, discountPercentage, loyaltyBonusPointsMultiplier } = useOrderItemsEligibleForCarHireBundle(order.id)

  const bonusPointsCampaigns = getLoyaltyBonusPointsCampaignTypes({
    bonusPoints: loyaltyBonusPointsMultiplier ?? 0,
    basePoints: 1,
    bonusStatusCredits: 0,
    statusCredits: 0,
  })
  const bonusPointsCampaign = bonusPointsCampaigns[0]
  const bonusPointsCampaignLabel = bonusPointsCampaign ? intl.formatMessage(LOYALTY_BONUS_POINTS_CAMPAIGN_LABEL[bonusPointsCampaign]) : undefined
  const hasDiscount = benefits?.has('discount')
  const hasBonusPoints = benefits?.has('loyalty_bonus_points')
  const placeLocationPhrase = place?.name ? formatPlacePhrase({ location: { name: place.name }, language: intl.locale, intent: 'location' }) : undefined

  const title = useMemo(() => {
    if (hasDiscount) {
      return intl.formatMessage(
        {
          id: 'Ljs4y1KogR7OpNUNX2iS',
          defaultMessage: '{discountPercentage}% off car hire for your trip to {placeName}',
          description: 'Heading for car hire upsell section showing discount percentage and destination',
        },
        { discountPercentage, placeName: placeLocationPhrase },
      )
    }

    if (hasBonusPoints) {
      return intl.formatMessage(
        {
          id: 'R2aZjhOJN1fy_Mkzklfw',
          defaultMessage: '{bonusPointsCampaignLabel} on car hire for your {placeName} trip',
          description: 'Heading for car hire upsell section showing bonus points campaign and destination',
        },
        { bonusPointsCampaignLabel, placeName: placeLocationPhrase },
      )
    }
  }, [hasDiscount, hasBonusPoints, discountPercentage, bonusPointsCampaignLabel, placeLocationPhrase, intl])

  const viewAllLabel = intl.formatMessage({
    id: 'Lz5mYR8HjfVZlPbvR6lK',
    defaultMessage: 'View all',
    description: 'Button text to view all car hire options in car hire section',
  })

  const dispatch = useAppDispatch()

  const onSearch = useCallback(() => {
    dispatch(pushWithRegion(searchLink))
  }, [searchLink, dispatch])

  return <Group direction="vertical" gap={24} desktopGap={40}>
    <LayoutContainer style={variant === 'card' ? { padding: 0 } : undefined}>
      <MainContainer direction="horizontal" gap={8} verticalAlign="center">
        {fetching && <LoadingBox style={{ width: 1000, height: 34 }} />}
        {!fetching && <Heading variant="heading2">{title}</Heading>}
        {fetching && <LoadingBox style={{ width: 48, height: 20 }} />}
        {!fetching && <ViewAllButtonWrapper direction="horizontal" horizontalAlign="end" verticalAlign="baseline">
          <TextButton kind="tertiary" size="medium" onClick={onSearch}>
            {viewAllLabel}
          </TextButton>
        </ViewAllButtonWrapper>}
      </MainContainer>
    </LayoutContainer>
    <CarHireBundledUpcomingOrderCarousel order={order} variant={variant} />
  </Group>
}

export default CarHireBundledUpcomingOrderCondensed
